import { useMemo, useState } from 'react';
import {
  ChevronLeftIcon, ChevronRightIcon, ExclamationTriangleIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { useStockWeek } from '../hooks/useJobCards';

// Who is logging the stock they used, and who is not.
//
// The board above answers "what is outstanding today". This answers the
// question behind it: a job stays at 'not_started' until the engineer records
// what came off the van, so assigned against logged — per engineer, per day —
// is the whole picture. Worst first, because the list exists to be acted on and
// nobody scrolls a hundred rows to find the person who logged nothing.
//
// Every figure is derived from the filtered set rather than the server's
// totals, so narrowing to one engineer re-reads the whole panel for that
// person instead of leaving headline numbers that quietly still mean everybody.
//
// Monday to Sunday (weekends added 01/10/2026). Saturday and Sunday are
// shaded so the working week still reads at a glance.
const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const isWeekend = (i) => i >= 5;

const pct = (v) => `${Math.round(Number(v || 0) * 100)}%`;
const num = (v) => Number(v || 0).toLocaleString('en-GB');

function rateTone(rate, assigned) {
  if (!assigned) return { bg: '#F9FAFB', fg: '#D1D5DB' };
  if (rate === 1) return { bg: '#DCFCE7', fg: '#166534' };
  if (rate >= 0.75) return { bg: '#ECFCCB', fg: '#3F6212' };
  if (rate >= 0.4) return { bg: '#FEF3C7', fg: '#92400E' };
  if (rate > 0) return { bg: '#FFEDD5', fg: '#9A3412' };
  return { bg: '#FEE2E2', fg: '#991B1B' };
}

const mondayOf = (d) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
};
const shift = (week, weeks) => {
  const x = new Date(`${week}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + weeks * 7);
  return x.toISOString().slice(0, 10);
};
const label = (d) => new Date(`${d}T00:00:00Z`)
  .toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

// The week as two lines with the shortfall shaded between them.
//
// A line rather than bars because the gap is the point: shaded, the unlogged
// work becomes the thing you look at, instead of a difference you have to work
// out by comparing two bar heights five times over.
// The week as grouped bars: assigned beside logged, one pair per day.
//
// Only days that have happened are charted. Jobs are booked days ahead, so a
// future day already carries assigned work with nothing logged against it -
// not a compliance failure, just work that has not happened yet, and charting
// it drags the whole week to the floor.
function WeekBars({ rows }) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
  const done = (d) => d.date <= today;

  // Scaled on elapsed days only, so a big booking later in the week does not
  // squash the days you can actually read.
  const max = Math.max(1, ...rows.filter(done).map((d) => d.assigned));

  return (
    <div className="mt-3 flex items-end gap-4">
      {rows.map((d, i) => (
        <div key={d.date} className={`flex flex-1 flex-col items-center rounded-md ${isWeekend(i) ? 'bg-gray-50' : ''}`}>
          <div className="flex h-32 w-full items-end justify-center gap-1.5">
            {done(d) && d.assigned ? (
              <>
                <div
                  className="w-1/3 rounded-t bg-gray-200"
                  style={{ height: `${Math.max((d.assigned / max) * 100, 2)}%` }}
                  title={`${d.assigned} assigned`}
                />
                <div
                  className="w-1/3 rounded-t bg-emerald-500"
                  style={{ height: `${Math.max((d.logged / max) * 100, d.logged ? 2 : 0)}%` }}
                  title={`${d.logged} logged`}
                />
              </>
            ) : (
              <div className="h-px w-2/3 bg-gray-100" />
            )}
          </div>
          <div className={`mt-2 text-xs font-medium ${done(d) && d.assigned ? 'text-gray-700' : 'text-gray-300'}`}>
            {DAY[i]}
          </div>
          <div className="text-[11px] tabular-nums text-gray-400">
            {done(d)
              ? (d.assigned ? `${num(d.logged)}/${num(d.assigned)}` : 'no jobs')
              : (d.assigned ? `${num(d.assigned)} booked` : 'to come')}
          </div>
          {done(d) && d.assigned ? (
            <div
              className="text-[11px] font-semibold tabular-nums"
              style={{ color: d.rate >= 0.9 ? '#166534' : (d.rate >= 0.6 ? '#92400E' : '#991B1B') }}
            >
              {pct(d.rate)}
            </div>
          ) : <div className="text-[11px]">&nbsp;</div>}
        </div>
      ))}
    </div>
  );
}

function Stat({ value, label: l, tone }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="text-lg font-bold tabular-nums" style={{ color: tone || '#111827' }}>{value}</div>
      <div className="text-xs font-medium text-gray-500">{l}</div>
    </div>
  );
}

function StockWeek() {
  const [week, setWeek] = useState(() => mondayOf(new Date().toISOString().slice(0, 10)));
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [who, setWho] = useState('');

  const { data, isLoading, isError } = useStockWeek(week);

  // Stable identity, or every memo below recomputes on each render.
  const days = useMemo(() => data?.days || [], [data]);
  const allEngineers = useMemo(() => data?.engineers || [], [data]);

  const rows = useMemo(() => {
    let all = allEngineers;
    const q = who.trim().toLowerCase();
    if (q) all = all.filter((e) => `${e.engineer} ${e.email}`.toLowerCase().includes(q));
    if (onlyGaps) all = all.filter((e) => e.logged < e.assigned);
    return all;
  }, [allEngineers, onlyGaps, who]);

  // Recomputed from whatever is on screen, so the chart and the headline
  // numbers always describe the same people as the table.
  const view = useMemo(() => {
    const byDay = days.map((d) => {
      const cells = rows.map((e) => e.days[d]).filter(Boolean);
      const assigned = cells.reduce((n, c) => n + c.assigned, 0);
      const logged = cells.reduce((n, c) => n + c.logged, 0);
      return { date: d, assigned, logged, rate: assigned ? logged / assigned : 0 };
    });
    const assigned = rows.reduce((n, e) => n + e.assigned, 0);
    const logged = rows.reduce((n, e) => n + e.logged, 0);
    return {
      byDay,
      totals: {
        engineers: rows.length,
        assigned,
        logged,
        not_started: assigned - logged,
        rate: assigned ? logged / assigned : 0,
        zero_loggers: rows.filter((e) => e.assigned > 0 && e.logged === 0).length,
        full_loggers: rows.filter((e) => e.assigned > 0 && e.logged === e.assigned).length,
      },
    };
  }, [rows, days]);

  const t = view.totals;
  const filtered = Boolean(who.trim()) || onlyGaps;

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-800">Stock logging — week of {label(week)}</h2>
          <p className="text-xs text-gray-500">
            Jobs assigned against jobs where the engineer logged what they used.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* A search box rather than a dropdown: 137 engineers is too many to
              pick from, and people search by the part of the name they know. */}
          <div className="relative">
            <input
              type="text"
              value={who}
              onChange={(e) => setWho(e.target.value)}
              list="stockweek-engineers"
              placeholder="Filter by engineer…"
              className="w-52 rounded-md border border-gray-300 px-2.5 py-1.5 pr-7 text-sm focus:border-gray-400 focus:outline-none"
            />
            <datalist id="stockweek-engineers">
              {allEngineers.map((e) => <option key={e.email} value={e.engineer} />)}
            </datalist>
            {who ? (
              <button
                type="button"
                onClick={() => setWho('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                title="Clear"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setWeek(shift(week, -1))}
            className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-50"
            title="Previous week"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setWeek(mondayOf(new Date().toISOString().slice(0, 10)))}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            This week
          </button>
          <button
            type="button"
            onClick={() => setWeek(shift(week, 1))}
            className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-50"
            title="Next week"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isError && <p className="px-4 py-6 text-sm text-red-500">Could not load stock logging.</p>}
      {isLoading && <p className="px-4 py-6 text-sm text-gray-400">Loading…</p>}

      {data && (
        <div className="p-4">
          {filtered ? (
            <p className="mb-3 text-xs text-gray-500">
              Showing <strong>{num(t.engineers)}</strong> of {num(allEngineers.length)} engineers
              {who.trim() ? <> matching “{who.trim()}”</> : null}
              {onlyGaps ? ' with unlogged jobs' : null}. Every figure below covers only these.
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3 self-start sm:grid-cols-5">
            <Stat value={num(t.assigned)} label="Jobs assigned" />
            <Stat value={num(t.logged)} label="Stock logged" tone="#166534" />
            <Stat value={num(t.not_started)} label="Not logged" tone={t.not_started ? '#991B1B' : undefined} />
            <Stat value={t.assigned ? pct(t.rate) : '—'} label="Logged" tone={t.rate >= 0.9 ? '#166534' : '#92400E'} />
            <Stat
              value={num(t.zero_loggers)}
              label="Logged nothing"
              tone={t.zero_loggers ? '#991B1B' : '#166534'}
            />
          </div>

          <div className="mt-5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Assigned vs logged, by day
            </h3>
            <div className="mt-2">
              <WeekBars rows={view.byDay} />
            </div>
            <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-500">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-sm bg-gray-200" />
                assigned
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />
                logged
              </span>
            </div>
          </div>

          {t.zero_loggers ? (
            <div className="mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <p className="text-xs text-red-800">
                <strong>
                  {t.zero_loggers} {t.zero_loggers === 1 ? 'engineer' : 'engineers'} logged nothing all week
                </strong>
                {' '}despite having jobs assigned — they are at the top of the table below. That stock
                has left a van without being recorded anywhere.
              </p>
            </div>
          ) : null}

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                By engineer — logged / assigned
              </h3>
              <button
                type="button"
                onClick={() => setOnlyGaps((v) => !v)}
                className="text-xs font-medium text-gray-500 hover:text-gray-800"
              >
                {onlyGaps ? 'Show everyone' : 'Only those with gaps'}
              </button>
            </div>

            <div className="mt-2 max-h-[30rem] overflow-auto rounded-lg border border-gray-200">
              <table className="min-w-full border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Engineer
                    </th>
                    {days.map((d, i) => (
                      <th
                        key={d}
                        className={`border-b border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 ${isWeekend(i) ? 'bg-gray-100' : ''}`}
                      >
                        {DAY[i]}
                        <div className="text-[10px] font-normal text-gray-400">{label(d)}</div>
                      </th>
                    ))}
                    <th className="border-b border-gray-200 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Week
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.email} className="hover:bg-gray-50/60">
                      <td className="border-b border-gray-100 px-3 py-1.5">
                        <button
                          type="button"
                          onClick={() => setWho(who.trim() === e.engineer ? '' : e.engineer)}
                          className="text-left font-medium text-gray-800 hover:underline"
                          title="Filter to this engineer"
                        >
                          {e.engineer}
                        </button>
                        {e.region ? <div className="text-[11px] text-gray-400">{e.region}</div> : null}
                      </td>
                      {days.map((d, di) => {
                        const c = e.days[d];
                        const tone = rateTone(c && c.assigned ? c.logged / c.assigned : 0, c ? c.assigned : 0);
                        return (
                          <td key={d} className={`border-b border-gray-100 px-1 py-1 text-center ${isWeekend(di) ? 'bg-gray-50' : ''}`}>
                            {c ? (
                              <span
                                className="inline-block min-w-[3rem] rounded px-1.5 py-1 text-xs font-semibold tabular-nums"
                                style={{ background: tone.bg, color: tone.fg }}
                                title={`${c.logged} logged of ${c.assigned} assigned`}
                              >
                                {c.logged}/{c.assigned}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">·</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="border-b border-gray-100 px-3 py-1.5 text-right">
                        <span
                          className="text-sm font-bold tabular-nums"
                          style={{ color: rateTone(e.rate, e.assigned).fg }}
                        >
                          {pct(e.rate)}
                        </span>
                        <div className="text-[11px] tabular-nums text-gray-400">
                          {e.logged}/{e.assigned}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!rows.length ? (
                    <tr>
                      <td colSpan={days.length + 2} className="px-3 py-10 text-center text-gray-400">
                        {who.trim()
                          ? `No engineer matching “${who.trim()}” had jobs this week.`
                          : (onlyGaps ? 'Everyone logged everything this week.' : 'No jobs assigned this week.')}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <p className="mt-2 text-[11px] text-gray-400">
              A job counts as logged once its stock status reaches complete. A blank cell means no jobs
              were assigned that day. Click a name to filter to that engineer.
              {' '}{num(t.full_loggers)} of {num(t.engineers)} shown logged every job this week.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockWeek;
